// EN
export const hotelRoomsManager = {
  header: {
    eyebrow: "Hotel rooms",
    title: "Room management",
    subtitle:
      "Create room types and generate the physical units with their numbering. These rooms will sync with Channex.",
    addRoom: "Add room",
  },
  states: {
    loading: "Loading rooms...",
    emptyTitle: "No rooms yet",
    emptySubtitle:
      "Create the first room type to start setting up rates and availability in Channex.",
    addFirstRoom: "Add first room",
  },
  card: {
    roomsBadge: "{count} room | {count} rooms",
    maxGuests: "Max. {count} guests",
    editTitle: "Edit room",
    deleteTitle: "Delete room",
    adults: "{count} adult | {count} adults",
    children: "{count} child | {count} children",
    infants: "{count} infant | {count} infants",
    units: "Units",
  },
  physical: {
    title: "Physical rooms",
    subtitle:
      "Edit the number or name of each room, or move it to another type. Its bookings go with it. Drag them by the handle to order them within their type: that is the order you'll see in the calendar.",
    roomsCount: "{count} room | {count} rooms",
    savingOrder: "Saving order…",
    selectAll: "Select all",
    clearSelection: "Clear selection",
    dragToOrder: "Drag to reorder",
    selectRoom: "Select {label}",
    roomFallback: "room",
    codePlaceholder: "No. / code",
    namePlaceholder: "Name",
  },
  bar: {
    selectedCount: "room selected | rooms selected",
    clearSelection: "Clear selection",
    changeType: "Change type",
  },
  modal: {
    createTitle: "New room",
    editTitle: "Edit room",
    createSubtitle: "Define the type and generate the physical units",
    editSubtitle: "Update the room type details",
    basicInfo: "Basic information",
    nameLabel: "Room name",
    namePlaceholder: "E.g. Double Room, Junior Suite…",
    descriptionLabel: "Description",
    optional: "(optional)",
    descriptionPlaceholder: "Sea view, terrace, minibar…",
    inventorySection: "Inventory and capacity",
    countLabel: "Number of rooms",
    countHintZero:
      "Created with no rooms: you can then assign existing rooms to it from the physical rooms list.",
    countHint: "Total physical units of this type",
    capacityLabel: "Total capacity (guests)",
    capacityHint: "Maximum allowed across adults + children + infants",
    priceLabel: "Price per night",
    priceHint:
      "This creates its standard rate. You can fine-tune it later in “Hotel rates”.",
    adultsLabel: "Adults",
    childrenLabel: "Children",
    infantsLabel: "Infants",
    numberingSection: "Room numbering",
    toggleOn: "On",
    toggleOff: "Off",
    numberingHintCreate:
      "Rooms are created with an automatic code. You can change it one by one in the physical rooms list, or set the numbering from “Edit” so the next ones are born with it.",
    prefixLabel: "Code prefix",
    prefixPlaceholder: "E.g. 10  or  A-",
    prefixHint: 'E.g. prefix "10" + start 1 → 101, 102…',
    startNumberLabel: "Start number",
    paddingLabel: "Padding",
    paddingHint: "3 → 001, 002…",
    nameModeLabel: "Physical room names",
    nameModeType: "Type only",
    nameModeTypeExample: "Double",
    nameModeTypeCode: "Type + code",
    nameModeTypeCodeExample: "Double 101",
    preview: {
      lead: "We'll create",
      roomsNoun: "room | rooms",
      withNumbering: "with this numbering. Example:",
    },
    numberingHintKeep:
      "Existing rooms keep their number. This numbering will apply to the ones you add from now on.",
    numberingHintZero:
      "With no rooms there's nothing to number. Assign existing rooms to it from the physical rooms list.",
    numberingHintDisabled:
      "Physical rooms won't be generated automatically. You can enable it whenever you want.",
    cancel: "Cancel",
    saving: "Saving…",
    creating: "Creating…",
    saveChanges: "Save changes",
    createRoom: "Create room",
  },
  delete: {
    title: "Delete room",
    subtitle: "This action can't be undone",
    confirmCopy: "Type the room name to confirm:",
    placeholder: "Room name",
    cancel: "Cancel",
    deleting: "Deleting...",
    confirm: "Delete",
  },
  change: {
    title: "Change the type {target}",
    subtitle: "The room number doesn't change",
    newTypeLabel: "New type",
    newTypePlaceholder: "Choose which type it moves to…",
    blocksHead: "One booking is blocking the change | {count} bookings are blocking the change",
    moves: "Moves to the new type",
    stays: "Stays in its current type",
    addMissing: "Add {labels} to the change and retry",
    stockLabel: "Rooms on sale",
    checkingReservations: "Checking which bookings are in {target}…",
    thisRoom: "this room",
    theseRooms: "these rooms",
    theRoom: "the room",
    theRooms: "the rooms",
    noReservations: "There are no bookings in {target}.",
    reservationsLeave: "booking leaves | bookings leave",
    with: "with {target}",
    moreReservations: "and {count} more booking | and {count} more bookings",
    greyLegend: "The rooms in grey stay in their current type.",
    retry: "Retry",
    cancel: "Cancel",
    applying: "Changing…",
    confirm: "Yes, change type",
    titleTargetOne: "of room {label}",
    titleTargetMany: "of {count} room | of {count} rooms",
    textZero: "These rooms are already that type: choose another to move them.",
    textOne:
      "The room keeps its number and its bookings: only the type it's sold as changes. Where it is now there will be one room fewer on sale, and in the new type, one more.",
    textMany:
      "The {count} rooms keep their number and their bookings: only the type they're sold as changes. Where they are now there will be {count} rooms fewer on sale, and in the new type, {count} more.",
  },
  blockTitles: {
    huespedDentro: "A guest is checked in",
    reservaRepartida: "The booking occupies other rooms",
    reservaSinAsignar: "The booking is missing some of its rooms",
    fallback: "Can't be moved",
  },
  groups: {
    noType: "No type assigned",
  },
  reservation: {
    noName: "No name",
  },
  toasts: {
    roomsChanged: "Room changed type | {count} rooms changed type",
    changedSyncing: "{done} · updating channels",
    created: "Room created successfully",
    updated: "Room updated",
    deleted: "Room deleted",
  },
  errors: {
    generic: "Something went wrong.",
    loadRooms: "Error loading the rooms",
    updateQty: "Couldn't update the quantity",
    savePhysicalRoom: "Couldn't save the room",
    saveOrder: "Couldn't save the room order.",
    checkReservations: "Couldn't check which bookings are in these rooms.",
    changeType: "Couldn't change the room type.",
    noProperty: "Couldn't get the accommodation. Please try again.",
    saveRoom: "Error saving the room",
    deleteRoom: "Error deleting",
  },
};
